import axios from "axios";
import { IssueTrackerInterface } from "./IssueTrackerInterface";

export class JiraTracker extends IssueTrackerInterface {
    constructor(config) {
        super(config);
        this.proxyUrl = config.proxyUrl;
        this.jiraConfig = {
            domain: config.domain,
            email: config.email,
            apiToken: config.apiToken,
            projectKey: config.projectKey,
            projectId: config.projectId,
            issueType: config.issueType,
        };
    }

    /**
     * Load Project ID dynamically from Jira
     */
    async loadProjects() {
        const response = await axios.get(`${this.proxyUrl}`, {
            params: {
                csurl: `https://${this.jiraConfig.domain}/rest/api/3/project`,
            },
            headers: this.getAuthHeaders().headers,
        });
        return response.data;
    }

    /**
     * Load Issue Type ID dynamically from Jira
     */
    async loadIssueType(projectId) {
        const response = await axios.get(`${this.proxyUrl}`, {
            params: {
                csurl: `https://${this.jiraConfig.domain}/rest/api/3/issuetype/project`,
                projectId: projectId,
            },
            headers: this.getAuthHeaders().headers,
        });

        return response.data;
    }

    /**
     * Get the URL for the issue in Jira
     * @param issueKey
     * @returns {string}
     */
    getIssueUrl(issueKey) {
       return `https://${this.jiraConfig.domain}/browse/${issueKey}`;
    }

    /**
     * Delete the issue from Jira
     * @param issueKey
     */
    async deleteIssue(issueKey) {
        const response = await axios.delete(`${this.proxyUrl}`, {
            params: {
                csurl: `https://${this.jiraConfig.domain}/rest/api/3/issue/${issueKey}`,
            },
            headers: this.getAuthHeaders().headers,
        });
        return response.data;
    }

    /**
     * Load all issues from Jira
     * @param issueKey
     */
    async getIssue(issueKey) {
        // Fetch the latest issue data from Jira
        const response = await axios.get(`${this.proxyUrl}`, {
            params: {
                csurl: `https://${this.jiraConfig.domain}/rest/api/3/issue/${issueKey}`,
                fields: "*all",
            },
            headers: this.getAuthHeaders().headers,
        });

        const jiraIssue = response.data.fields;

        // QualityAspect aus Labels extrahieren
        const qualityAspectLabel = jiraIssue.labels.find((label) =>
            label.startsWith("QA_")
        );
        const qualityAspect = qualityAspectLabel ? qualityAspectLabel.replace("QA_", "") : null;


        const jiraTask = {
            summary:    jiraIssue.summary,
            dueDate: jiraIssue.duedate,
            assignee: jiraIssue.assignee?.accountId,
            description: jiraIssue.description.content[0].content[0].text,
            parentIssue: jiraIssue.parent?.key,
            labels: jiraIssue.labels,
            qualityAspect: qualityAspect,
        }
        return jiraTask;
    }

    /**
     * Update an existing issue in Jira
     * @param {string} issueKey - Jira issue key (e.g., "ST-1")
     * @param {Object} issue - Updated issue details
     */
    async updateIssue(issueKey, issue) {
        if (!this.jiraConfig.projectId || !this.jiraConfig.issueType) {
            throw new Error("Project or Issue Type is not configured.");
        }

        // Filter fields to include only the dirty ones
        const dirtyFields = {};
        const fieldMapping = {
            summary: "summary",
            dueDate: "duedate",
            assignee: "assignee",
            description: "description",
            parentIssue: "parent",
            labels: "labels",
            qualityAspect: "qualityAspect",
        };

        for (const [field, jiraField] of Object.entries(fieldMapping)) {
            if (issue.dirty[field]) {
                if (field === "qualityAspect") {
                    // Labels aktualisieren
                    const updatedLabels = issue.labels.filter(
                        (label) => !label.startsWith("QA_")
                    );
                    updatedLabels.push(`QA_${issue[field]}`);
                    dirtyFields["labels"] = updatedLabels;
                } else if (field === "description") {
                    // Special formatting for description
                    dirtyFields[jiraField] = {
                        content: [
                            {
                                content: [
                                    {
                                        text: issue[field],
                                        type: "text",
                                    },
                                ],
                                type: "paragraph",
                            },
                        ],
                        type: "doc",
                        version: 1,
                    };
                } else if (field === "dueDate") {
                    // Format date field
                    dirtyFields[jiraField] = this.formatDateToYYYYMMDD(issue[field]);
                } else if (field === "parentIssue") {
                    // Handle parent issue key
                    dirtyFields[jiraField] = { key: issue[field] };
                } else if (field === "assignee") {
                    // Handle assignee ID
                    dirtyFields[jiraField] = { id: issue[field] };
                } else {
                    dirtyFields[jiraField] = issue[field];
                }
            }
        }

        // Construct the update request
        const request = {
            csurl: `https://${this.jiraConfig.domain}/rest/api/3/issue/${issueKey}`,
            fields: dirtyFields,
        };

        // Log dirty fields for debugging
        console.log("Updating issue with dirty fields:", dirtyFields);

        // Send the update request
        const response = await axios.put(this.proxyUrl, request, this.getAuthHeaders());
        return response.data;
    }

    /**
     * Create a new issue in Jira
     */
    async createIssue(issue) {
        if (!this.jiraConfig.projectId || !this.jiraConfig.issueType) {
            throw new Error("Project or Issue Type is not configered.");
        }

        const labels = issue.labels || [];
        if (issue.qualityAspect) {
            labels.push(`QA_${issue.qualityAspect}`);
        }

        const request = {
            csurl: `https://${this.jiraConfig.domain}/rest/api/3/issue`,
            fields: {
                project: { id: this.jiraConfig.projectId },
                issuetype: { id: this.jiraConfig.issueType },
                summary: issue.summary,
                duedate: this.formatDateToYYYYMMDD(issue.dueDate),
                assignee: {
                    id: issue.assignee
                },
                description: {
                    content: [
                        {
                            content: [
                                {
                                    text: issue.description,
                                    type: "text",
                                },
                            ],
                            type: "paragraph",
                        },
                    ],
                    type: "doc",
                    version: 1,
                },
                parent: {
                    key: issue.parentIssue
                },
                labels: labels,
            },
        };

        const response = await axios.post(this.proxyUrl, request, this.getAuthHeaders());

        return response.data.key;
    }

    formatDateToYYYYMMDD(date) {
        if(!(date instanceof Date)) {
            date = new Date(date);
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Get all assignable users for the project
     * @returns {Promise<Array>}
     */
    async getAssignees() {
        try {
            const request = {
                params: {
                    csurl: `https://${this.jiraConfig.domain}/rest/api/3/user/assignable/search`,
                    project: this.jiraConfig.projectKey,
                },
                headers: this.getAuthHeaders().headers,
            };
            console.log("Fetching assignees for project:", this.jiraConfig.projectKey, request);
            const response = await axios.get(this.proxyUrl, request);
            console.log("getAssignees Response Data:", response.data);
            return response.data.map(user => ({ id: user.accountId, name: user.displayName }));
        } catch (error) {
            console.error("getAssignees Error:", error.response?.data || error.message);
            throw error; // Re-throw the error
        }
    }

    /**
     * Get parent issues (epics) in the project
     * @returns {Promise<Array>}
     */
    async getParentIssues() {
        const response = await axios.get(this.proxyUrl, {
            params: {
                csurl: `https://${this.jiraConfig.domain}/rest/api/3/search`,
                jql: `project=${this.jiraConfig.projectKey} AND issuetype=Epic`,
                fields: "summary,key",
            },
            headers: this.getAuthHeaders().headers,
        });
        return response.data.issues.map(issue => ({
            id: issue.id,
            key: issue.key,
            summary: issue.fields.summary,
        }));
    }

    /**
     * Get Authorization Headers
     * @returns {Object}
     */
    getAuthHeaders() {
        const authHeader = `Basic ${btoa(`${this.jiraConfig.email}:${this.jiraConfig.apiToken}`)}`;
        console.log("authHeader", authHeader)
        return {
            headers: {
                Authorization: authHeader,
                "Content-Type": "application/json",
            },
        };
    }
}